import {Injectable} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {Issue} from '../model/issue';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ApiRouteConfig} from '../../../config/api.route.config';
import * as FileSaver from '../../../../../node_modules/file-saver/FileSaver.min.js';
import {TranslateService} from '@ngx-translate/core';
import {IssueLogEntry} from './model/issue-log-entry';
import {IssueCategoryType, IssueType} from '../../common/enums/enums';
import {HttpParamsService} from '../../common/services/http-params.service';
import {catchError, map, tap} from 'rxjs/operators';
import {PaginationResponse} from '../../common/model/pagination-response';
import {
  Multimedium,
  MultimediumContract, unmarshall
} from '@npm.chapps.com/chapps.common.angular.library';
import {MergeRequest} from './dto/merge-request';

@Injectable()
export class BuildingIssuesService {
  resolved$: Subject<number[]> = new Subject<number[]>();
  updated$: Subject<Issue> = new Subject<Issue>();

  constructor(private httpClient: HttpClient, private config: ApiRouteConfig, private translate: TranslateService,
              private httpParamsService: HttpParamsService) {
  }

  public getBuildingIssues(buildingId: number|undefined, httpParams: HttpParams, pageNumber: number, pageSize: number): Observable<PaginationResponse<Issue>> {
    const url = this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES, [`${buildingId}`]);
    const params = httpParams
      .append('pageNumber', pageNumber.toString())
      .append('pageSize', pageSize.toString());
    return this.httpClient.get<PaginationResponse<Issue>>(url, {params});
  }



  public addIssue(issue: Issue): Observable<Issue> {
    return this.httpClient.post<Issue>(this.config.BUILDING_ISSUES_DETAILS, issue)
      .pipe(tap((iss) => this.updated$.next(iss)));
  }

  public addNewPhoto(issueId: number, file: File): Observable<Multimedium> {
    let formData: FormData = new FormData();
    formData.append('issuePhoto', file, file.name);
    return this.httpClient.put<Multimedium>(
      this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS_PHOTO, [issueId.toString()]),formData);
  }

  public addNewVideo(issueId: number, videoFile: File, snapshotFile: File): Observable<Multimedium> {
    const formData: FormData = new FormData();
    formData.append('videoFile', videoFile, videoFile.name);

    if (snapshotFile) {
      formData.append('snapshotFile', snapshotFile, snapshotFile.name);
    }

    return this.httpClient.put<Multimedium>(
      this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS_VIDEO, [issueId.toString()]),formData);
  }

  public deletePhoto(issueId: number, photoId: number): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS_MEDIA_DELETE, [issueId.toString(), photoId.toString()]));
  }

  public downloadPhoto(issueId: number, photoId: number) {

  }

  public getPhotos(issueId: number): Observable<Multimedium[]> {
    return this.httpClient.get<MultimediumContract[]>(this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS_MEDIA, [issueId.toString()]))
      .pipe(map(photos => photos.map(unmarshall)));
  }

  public update(issue: Issue): Observable<Issue> {
    return this.httpClient.put<Issue>(
      this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS, []), issue)
      .pipe(tap(() => this.updated$.next(issue)));
  }

  public merge(request: MergeRequest): Observable<Issue> {
    return this.httpClient.post<Issue>(this.config.BUILDING_ISSUES_MERGE, request).pipe(tap(() => this.resolved$.next(request.issuesToMerge)));
  }

  public resolve(ids: number[]): Observable<boolean> {
    return this.httpClient.post<boolean>(this.config.BUILDING_ISSUES_RESOLVE, ids).pipe(tap(() => this.resolved$.next(ids)));
  }

  public resolveSingle(id: number, comment: string): Observable<boolean> {
    return this.httpClient.post<boolean>(
      this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUE_RESOLVE, [id.toString()]),
      {comment}).pipe(tap(() => this.resolved$.next([id])));
  }

  public cancelIssue(id: number, comment: string): Observable<boolean> {
    return this.httpClient.post<boolean>(
      this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUE_CANCEL, [id.toString()]),
      {comment}).pipe(tap(() => this.updated$.next()));
  }

  public snoozeIssue(id: number, snoozeUntil: Date, comment: string): Observable<boolean> {
    return this.httpClient.post<boolean>(
      this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUE_SNOOZE, [id.toString()]),
      {comment, snoozeUntil}).pipe(tap(() => this.updated$.next()));
  }

  public export(buildingId: number, ids: number[]): Observable<boolean> {
    const url = this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS_EXPORT, [`${buildingId}`]);
    return this.httpClient.post(url, ids, {observe: 'response', responseType: 'arraybuffer'})
      .pipe(
        map((response) => {
          const blob = new Blob([response.body], {type: response.headers.get('content-type')});
          FileSaver.saveAs(blob, response.headers.get('filename'));
          return true;
        }),
        catchError(error => {
          return of(true);
        })
    );
  }

  public exportIssuesFromMultipleBuildings(ids: number[]): Observable<boolean> {
    const url = this.config.BUILDING_ISSUES_EXPORT_BY_IDS;
    return this.httpClient.post(url, ids, {observe: 'response', responseType: 'arraybuffer'})
      .pipe(
        map((response) => {
          const blob = new Blob([response.body], {type: response.headers.get('content-type')});
          FileSaver.saveAs(blob, response.headers.get('filename'));
          return true;
        }),
        catchError(error => {
          return of(true);
        })
    );
  }


  public getIssue(id: number): Observable<Issue> {
    return this.httpClient.get<Issue>(this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS_ID, [id.toString()]));
  }

  public getLog(issueId: number): Observable<IssueLogEntry[]> {
    return this.httpClient.get<IssueLogEntry[]>(this.config.getRouteWithInjectedParams(this.config.BUILDING_ISSUES_DETAILS_LOGS, [issueId.toString()]));
  }

  public getIssueObservationValues(categoryType: IssueCategoryType, issueType: IssueType, templateCheckPointId: number, templateStructureId: number): Observable<string[]> {
    return this.httpClient.get<string[]>(this.config.BUILDING_ISSUES_OBSERVATION_VALUES,
      { params: this.httpParamsService.createForIssueObservationValues(categoryType, issueType, templateCheckPointId, templateStructureId) });
  }

  /**
   * Opens the generated
   * @param issueId
   */
  public generateReport(issueId: number): Observable<Blob> {
    const url = this.config.getRouteWithInjectedParams(
      this.config.BUILDING_ISSUE_REPORT,
      [`${issueId}`]
    );
    return this.httpClient.get<Blob>(url, {
      responseType: 'blob' as 'json'
    });
  }

  public generateReportForIssues(issueIds: number[]): Observable<Blob> {
    return this.httpClient.post<Blob>(this.config.BUILDING_ISSUES_REPORT, issueIds, {
      responseType: 'blob' as 'json'
    });
  }
}
